// import React, { useState } from 'react';
// import { CheckCircle2, FolderOpen } from 'lucide-react';
// import Drawer from '../components/Drawer';
// import TransactionTable from '../components/TransactionTable';
// import Footer from '../components/Footer';
// import { useActiveProject } from '../context/ActiveProjectContext';

// const TransactionsPage = () => {
//   const { selectedProject, selectedProjectId, openProjectPicker } = useActiveProject();
//   const [refreshKey, setRefreshKey] = useState(0);

//   const pageContent = (
//     <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
//       <div className="mb-6">
//         <h1 className="text-2xl font-bold text-slate-900">Transactions</h1>
//         <p className="text-slate-500 text-sm mt-1">
//           Only the selected project history is shown below
//         </p>
//       </div>

//       {selectedProject ? (
//         <div
//           className="rounded-2xl border p-4 mb-6 flex items-center justify-between gap-4"
//           style={{ borderColor: '#16a34a', background: '#f0fdf4' }}
//         >
//           <div className="flex items-center gap-3 min-w-0">
//             <CheckCircle2 size={20} color="#16a34a" />
//             <div className="min-w-0">
//               <p className="text-sm font-bold text-slate-800 truncate">
//                 {selectedProject.name}
//               </p>
//               <p className="text-xs text-slate-500 truncate">
//                 Selected project history
//               </p>
//             </div>
//           </div>

//           <div className="flex items-center gap-2 shrink-0">
//             <FolderOpen size={18} color="#c44569" />
//             <span className="text-xs font-semibold text-slate-500">
//               Project ID: {projectId}
//             </span>
//           </div>
//         </div>
//       ) : (
//         <div
//           className="rounded-2xl border p-5 mb-6"
//           style={{ borderColor: '#f9b8ca', background: '#fff6f8' }}
//         >
//           <p className="text-sm text-slate-600">
//             No project selected. Please choose a project from the popup first.
//           </p>
//           <button
//             type="button"
//             onClick={openProjectPicker}
//             className="mt-3 px-4 py-2 rounded-xl text-xs font-semibold text-white"
//             style={{ background: '#c44569' }}
//           >
//             Choose Project
//           </button>
//         </div>
//       )}

//       <TransactionTable
//         key={selectedProjectId || 'no-project'}
//         refreshSignal={refreshKey}
//         projectFilter={selectedProjectId}
//       />

//       <Footer />
//     </div>
//   );

//   return <Drawer pageHeader={pageContent} />;
// };

// export default TransactionsPage;


import React, { useState } from 'react';
import { CheckCircle2, FolderOpen } from 'lucide-react';
import Drawer from '../components/Drawer';
import TransactionTable from '../components/TransactionTable';
import Footer from '../components/Footer';
import NewProjectFormScreen from '../components/NewProjectFormScreen';
import { useActiveProject } from '../context/ActiveProjectContext';
import { useAuth } from '../context/AuthContext';
import PermissionNoticeCard from '../components/PermissionNoticeCard';

const TransactionsPage = () => {
  const {
    canCreateProjects,
    canCreateTransactions,
    canEditTransactions,
    canDeleteTransactions,
    canExportTransactions,
  } = useAuth();
  const {
    selectedProject,
    selectedProjectId,
    openProjectPicker,
    refreshProjects,
    createAndSelectProject,
  } = useActiveProject();

  const [refreshKey, setRefreshKey] = useState(0);
  const [projectFormOpen, setProjectFormOpen] = useState(false);
  const hasTransactionAccess = canCreateTransactions || canEditTransactions || canDeleteTransactions || canExportTransactions;

  const pageContent = (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {!hasTransactionAccess ? (
        <div className="mb-6">
          <PermissionNoticeCard
            title="Transaction Access Restricted"
            message="You currently do not have permission from your Organization Head or Organization Admin to access this feature."
            hint="The layout and profile remain available, but transaction actions stay hidden until access is granted."
          />
        </div>
      ) : null}

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Transactions</h1>
            <p className="text-slate-500 text-sm mt-1">
              Only the selected project history is shown below
            </p>
          </div>
          {hasTransactionAccess ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={openProjectPicker}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
                style={{ background: '#c44569' }}
              >
                Choose Project
              </button>
              {canCreateProjects ? (
                <button
                  type="button"
                  onClick={() => setProjectFormOpen(true)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
                  style={{ background: '#0f766e' }}
                >
                  Create New Project
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {/* Selected Project */}
      {!hasTransactionAccess ? (
        <div
          className="rounded-2xl border bg-white p-6 text-center mb-6"
          style={{ borderColor: '#f9b8ca' }}
        >
          <p className="text-sm text-slate-500">
            Transaction history is restricted for your account.
          </p>
        </div>
      ) : selectedProject ? (
        <div
          className="rounded-2xl border p-4 mb-6 flex items-center justify-between gap-4"
          style={{
            borderColor: '#16a34a',
            background: '#f0fdf4',
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <CheckCircle2 size={20} color="#16a34a" />

            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-800 truncate">
                {selectedProject?.name || 'Selected Project'}
              </p>

              <p className="text-xs text-slate-500 truncate">
                Selected project history
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <FolderOpen size={18} color="#c44569" />

            <span className="text-xs font-semibold text-slate-500">
              Project ID: {selectedProjectId || 'N/A'}
            </span>
          </div>
        </div>
      ) : (
        <div
          className="rounded-2xl border p-5 mb-6"
          style={{
            borderColor: '#f9b8ca',
            background: '#fff6f8',
          }}
        >
          <p className="text-sm text-slate-600">
            No project selected. Please choose a project from the popup first.
          </p>

          <button
            type="button"
            onClick={openProjectPicker}
            className="mt-3 px-4 py-2 rounded-xl text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
            style={{ background: '#c44569' }}
          >
            Choose Project
          </button>
        </div>
      )}

      {/* Transactions Table */}
      {hasTransactionAccess && selectedProjectId ? (
        <TransactionTable
          key={selectedProjectId}
          refreshSignal={refreshKey}
          projectFilter={selectedProjectId}
          canCreateTransactions={canCreateTransactions}
          canEditTransactions={canEditTransactions}
          canDeleteTransactions={canDeleteTransactions}
          canExportTransactions={canExportTransactions}
        />
      ) : hasTransactionAccess ? (
        <div
          className="rounded-2xl border bg-white p-6 text-center"
          style={{ borderColor: '#f9b8ca' }}
        >
          <p className="text-sm text-slate-500">
            Please select a project to view transactions.
          </p>
        </div>
      ) : null}

      <Footer />

      <NewProjectFormScreen
        isOpen={projectFormOpen}
        onClose={() => setProjectFormOpen(false)}
        onSubmit={async (payload) => {
          await createAndSelectProject(payload);
          setProjectFormOpen(false);
          await refreshProjects();
          setRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );

  return <Drawer pageHeader={pageContent} />;
};

export default TransactionsPage;
